/**
 * LegacyRangeExplainer — Breakdown of legacy pricing methods.
 *
 * Shows how the "sweet spot" legacy range band is calculated.
 * All values in dollars.
 */

import { T } from '@/styles/tokens';

// ─── Types ────────────────────────────────────────────────────

export interface LegacyMethod {
  name: string;
  description: string;
  price: number;
}

export interface LegacyRangeExplainerProps {
  methods: LegacyMethod[];
  floorPrice: number;
  askingPrice: number;
  scaleMax: number;
}

// ─── Constants ────────────────────────────────────────────────

const MONO = '"Manrope", "JetBrains Mono", "DM Sans", monospace';
const SANS = '"Manrope", "DM Sans", -apple-system, sans-serif';
const TEAL = '#2C9C8C';
const TEAL_DARK = '#1F7A6D';
const NAVY = '#243A4E';

const DOT_COLORS = ['#5BBFBA', '#3DABA5', '#2A9089'];

function fmt(n: number): string {
  return '$' + n.toLocaleString('en-US');
}

// ─── Component ────────────────────────────────────────────────

export function LegacyRangeExplainer({
  methods, floorPrice, askingPrice, scaleMax,
}: LegacyRangeExplainerProps) {
  const sorted = [...methods].sort((a, b) => a.price - b.price);
  const low = sorted[0]?.price ?? 0;
  const high = sorted[sorted.length - 1]?.price ?? 0;

  const pctOf = (v: number) => Math.max(0, Math.min(100, (v / scaleMax) * 100));

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: 'rgba(61,171,165,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
          </svg>
        </div>
        <div>
          <div style={{ fontFamily: SANS, fontSize: 15, fontWeight: 700, color: T.text, letterSpacing: '-0.01em' }}>
            Common Methods
          </div>
          <div style={{ fontFamily: SANS, fontSize: 11, color: T.textMuted, marginTop: 1 }}>
            How the sweet spot is calculated
          </div>
        </div>
      </div>

      {/* Body text */}
      <p style={{
        fontFamily: SANS, fontSize: 13, color: T.textSecondary,
        lineHeight: 1.5, margin: '0 0 16px',
      }}>
        The curved band represents where three traditional pricing methods converge. TruePrice takes the lowest and highest to define the range. Your floor may fall inside or outside this range.
      </p>

      {/* Method list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {sorted.map((m, i) => (
          <div key={m.name} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 0',
            borderBottom: i < sorted.length - 1 ? `1px solid ${T.borderLight}` : 'none',
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: 4,
              background: DOT_COLORS[i] ?? TEAL,
              flexShrink: 0,
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: T.text }}>
                {m.name}
              </div>
              <div style={{ fontFamily: SANS, fontSize: 11, color: T.textMuted, marginTop: 1 }}>
                {m.description}
              </div>
            </div>
            <div style={{
              fontFamily: MONO, fontSize: 13, fontWeight: 700, color: T.text,
              flexShrink: 0,
            }}>
              {fmt(m.price)}
            </div>
          </div>
        ))}
      </div>

      {/* Summary mini-bar */}
      <div style={{ marginTop: 16, padding: '12px 0 4px' }}>
        <div style={{
          position: 'relative', height: 6, borderRadius: 3,
          background: T.borderLight, overflow: 'visible',
        }}>
          {/* Legacy range fill */}
          <div style={{
            position: 'absolute',
            left: `${pctOf(low)}%`,
            width: `${pctOf(high) - pctOf(low)}%`,
            height: '100%', borderRadius: 3,
            background: `linear-gradient(90deg, ${DOT_COLORS[0]}, ${DOT_COLORS[2]})`,
            opacity: 0.35,
          }} />

          {/* Floor marker */}
          <div style={{
            position: 'absolute',
            left: `${pctOf(floorPrice)}%`,
            top: -3, width: 3, height: 12,
            borderRadius: 1.5, background: NAVY, opacity: 0.7,
            transform: 'translateX(-1.5px)',
          }} />

          {/* Price pip */}
          <div style={{
            position: 'absolute',
            left: `${pctOf(askingPrice)}%`,
            top: -2, width: 10, height: 10,
            borderRadius: 5, background: TEAL,
            border: '2px solid white',
            transform: 'translateX(-5px)',
            boxShadow: '0 0 0 1px rgba(44,156,140,0.15)',
          }} />
        </div>

        {/* Mini-bar labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
          <span style={{ fontFamily: MONO, fontSize: 8, color: T.textMuted }}>$0</span>
          <span style={{ fontFamily: MONO, fontSize: 8, color: T.textMuted }}>
            {fmt(scaleMax)}
          </span>
        </div>
      </div>
    </div>
  );
}
